// CategoryDrawerShell.tsx
import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';

interface CategoryDrawerShellProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onRequestClose: () => void;
  onSave: () => void;
  saveDisabled: boolean;
  children: React.ReactNode;
  testId?: string;
  saveButtonText?: string;
}

export const CategoryDrawerShell: React.FC<CategoryDrawerShellProps> = ({
  open,
  title,
  subtitle,
  onRequestClose,
  onSave,
  saveDisabled,
  children,
  testId = 'category-drawer',
  saveButtonText = 'Save',
}) => {
  const { tokens } = useTheme<LensTheme>();
  const sp = tokens.semantic.spacing.desktop.gap;
  const color = tokens.semantic.color;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onRequestClose}
      ModalProps={{ keepMounted: false }}
      data-testid={testId}
      sx={{
        '& .MuiDrawer-paper': {
          width: '567px',
          padding: sp[3].value,
          backgroundColor: color.surface.default.value,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '100vh',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography
            component="h2"
            sx={{
              fontSize: '24px',
              fontWeight: tokens.semantic.fontWeight.emphasis.value,
              color: color.type.default.value,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontSize: '14px',
                color: color.type.muted.value,
                mt: '4px',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onRequestClose}
          aria-label="close"
          data-testid={`${testId}-close`}
          sx={{
            color: color.action.secondary.onSecondary.value,
            '&:hover': {
              backgroundColor: color.action.secondary.hoverFill.value,
            },
          }}
        >
          <Close />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          padding: sp[1].value,
        }}
      >
        {children}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pt: sp[3].value,
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          onClick={onRequestClose}
          data-testid={`${testId}-cancel-button`}
          sx={{
            textTransform: 'none',
            minWidth: '88px',
            height: '40px',
            cursor: 'pointer',
            borderRadius: tokens.semantic.radius.lg.value,
            borderColor: color.type.default.value,
            color: color.type.default.value,
            '&:hover': {
              borderColor: color.type.default.value,
              backgroundColor: 'transparent',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={saveDisabled}
          data-testid={`${testId}-save-button`}
          disableElevation
          sx={{
            textTransform: 'none',
            minWidth: '88px',
            height: '40px',
            cursor: 'pointer',
            borderRadius: tokens.semantic.radius.lg.value,
            backgroundColor: color.type.default.value,
            color: color.surface.default.value,
            '&:hover': {
              backgroundColor: color.type.default.value,
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              cursor: 'not-allowed',
              backgroundColor: color.ui.divider.default.value,
              color: color.type.muted.value,
            },
          }}
        >
          {saveButtonText}
        </Button>
      </Box>
    </Drawer>
  );
};
